import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Wifi, Car, Coffee, Dumbbell, Users, Bed, Eye, Square, Sparkles, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getHotelDetails, type HotelRoomOption, type HotelSearchParams, type HotelDetailsResponse } from '@/lib/hotels-api';
import type { Hotel } from '@/types/travel';
import { formatCurrency, formatMealPlan, isDisplayableMealPlan } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSubscriptionRules } from '@/hooks/useSubscriptionRules';
import { calculatePricing, type PricingResult } from '@/lib/subscription-rules';
import { useAuth } from '@/lib/auth';

interface RoomSelectionModalProps {
  hotel: Hotel;
  isOpen: boolean;
  onClose: () => void;
  onSelectRoom: (room: HotelRoomOption, priceType?: 'standard' | 'special', pricing?: PricingResult) => void;
  checkin: string;
  checkout: string;
  guests: HotelSearchParams['guests'];
}

const amenityIcons: Record<string, typeof Wifi> = {
  'WiFi': Wifi,
  'Free WiFi': Wifi,
  'Parking': Car,
  'Free Parking': Car,
  'Breakfast': Coffee,
  'Gym': Dumbbell,
  'Fitness Center': Dumbbell,
};

// Helper component for room price display with paywall support
interface RoomPriceDisplayProps {
  apiPrice: number;
  nights: number;
  rules: import('@/lib/subscription-rules').PricingRules;
  config: import('@/lib/subscription-rules').PricingConfig;
  isAuthenticated: boolean;
  serverStandardPrice?: number;
  serverSpecialPrice?: number;
}

function RoomPriceDisplay({ apiPrice, nights, rules, config, isAuthenticated, serverStandardPrice, serverSpecialPrice }: RoomPriceDisplayProps) {
  const pricing = useMemo(() => {
    const calc = calculatePricing(apiPrice, rules, config);
    if (serverStandardPrice != null) calc.standardPrice = serverStandardPrice;
    if (serverSpecialPrice != null) calc.specialPrice = serverSpecialPrice;
    calc.savings = Math.round((calc.standardPrice - calc.specialPrice) * 100) / 100;
    calc.savingsPercentage = calc.standardPrice > 0
      ? Math.round((calc.savings / calc.standardPrice) * 1000) / 10
      : 0;
    return calc;
  }, [apiPrice, rules, config, serverStandardPrice, serverSpecialPrice]);
  
  // Determine which price to display as the main price
  const appliedPrice = rules.canHaveSpecialPrice ? pricing.specialPrice : pricing.standardPrice;
  const appliedTotal = appliedPrice * nights;
  const totalSavings = rules.canHaveSpecialPrice ? pricing.savings * nights : 0;

  return (
    <div className="text-right ml-4">
      {/* Original/Crossed-out price */}
      {rules.paywallEnabled && isAuthenticated && (
        <div className="text-sm text-muted-foreground line-through">
          {formatCurrency(pricing.standardPrice)}/night
        </div>
      )}
      
      {/* Main price */}
      <div className="text-xl font-bold text-primary">
        {formatCurrency(appliedPrice)}
      </div>
      <div className="text-xs text-muted-foreground">per night</div>
      
      {/* Special price badge */}
      {rules.canHaveSpecialPrice && rules.paywallEnabled && (
        <Badge 
          variant="secondary" 
          className="mt-1 text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
        >
          <CheckCircle className="h-3 w-3 mr-1" />
          Subscriber price
        </Badge>
      )}
      
      {/* Total for stay */}
      {nights > 1 && (
        <div className="text-sm font-semibold mt-1">
          Total: {formatCurrency(appliedTotal)}
          {totalSavings > 0 && (
            <span className="text-emerald-600 text-xs ml-1">
              (save {formatCurrency(totalSavings)})
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function RoomSelectionModal({ 
  hotel, 
  isOpen, 
  onClose, 
  onSelectRoom,
  checkin,
  checkout,
  guests
}: RoomSelectionModalProps) {
  const [loading, setLoading] = useState(false);
  const [hotelWithRooms, setHotelWithRooms] = useState<HotelDetailsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Paywall: Get subscription rules
  const { user } = useAuth();
  const isAuthenticated = !!user;
  // VOY-512 (D3): resolve with the explicit 'hotel' product, and take `config` from the same
  // hook that resolves `rules` (was a separate scope-less getCachedConfigSync() → 'all').
  const { rules, config } = useSubscriptionRules({ product: 'hotel', hotelId: hotel?.id });
  
  // Reset state and fetch when modal opens or hotel changes
  useEffect(() => {
    // Don't do anything if modal is closed
    if (!isOpen || !hotel) {
      return;
    }
    
    // Validate required parameters
    if (!hotel.id || !checkin || !checkout || !guests || !Array.isArray(guests) || guests.length === 0) {
      console.error('[RoomSelectionModal] Missing required parameters:', {
        hotelId: hotel?.id,
        checkin,
        checkout,
        guests
      });
      setError('Missing required search parameters. Please try searching again.');
      setLoading(false);
      return;
    }
    
    // Reset state immediately when opening
    setLoading(true);
    setError(null);
    setHotelWithRooms(null);
    
    // Track if this effect is still valid
    let cancelled = false;
    
    getHotelDetails(String(hotel.id), {
      checkin,
      checkout,
      guests,
      residency: 'US',
      language: 'en',
      currency: 'USD',
    } as unknown as HotelSearchParams)
      .then((details) => {
        if (!cancelled) {
          setHotelWithRooms(details);
        }
      })
      .catch((err) => {
        // Ignore AbortError
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        if (!cancelled) {
          console.error('[RoomSelectionModal] Failed to fetch rooms:', err);
          setError('Unable to load room options. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hotel?.id, checkin, checkout, guests]);
  
  // Retry function for the Try Again button
  const handleRetry = () => {
    setError(null);
    setLoading(true);
    setHotelWithRooms(null);
    
    getHotelDetails(hotel.id, {
      checkin,
      checkout,
      guests,
      residency: 'US',
      language: 'en',
      currency: 'USD',
    } as unknown as HotelSearchParams)
      .then(setHotelWithRooms)
      .catch((err) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('Failed to fetch rooms:', err);
        setError('Unable to load room options. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  const calculateNights = () => {
    const start = new Date(checkin);
    const end = new Date(checkout);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const nights = calculateNights();
  const totalGuests = guests.reduce((sum, room) => sum + room.adults + room.children.length, 0);
  
  const formatCancellationPolicy = (text: string): string => {
    if (!text) return '';
    try {
      const isoRegex = /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:Z|[+-]\d{2}:?\d{2})?/;
      const match = text.match(isoRegex);
      if (match) {
        const date = new Date(match[0]);
        const formatted = new Intl.DateTimeFormat(undefined, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        }).format(date);
        let out = text.replace(match[0], formatted);
        out = out.replace(/\bbefore\b/i, 'until');
        return out;
      }
    } catch {
      // Ignore formatting errors and return original text
    }
    return text;
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden">
        <div className="max-h-[85vh] flex flex-col">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-xl">
            {hotel.name} - Select Your Room
          </DialogTitle>
          <div className="text-sm text-muted-foreground mt-2">
            {nights} night{nights > 1 ? 's' : ''} • {totalGuests} guest{totalGuests > 1 ? 's' : ''}
          </div>
        </DialogHeader>
        
        <ScrollArea className="flex-1 px-6 pb-6 h-[70vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={handleRetry} variant="outline">
                Try Again
              </Button>
            </div>
          ) : hotelWithRooms?.rooms && hotelWithRooms.rooms.length > 0 ? (
            <div className="space-y-4 mt-4">
              {hotelWithRooms.rooms.map((room) => {
                // Compute pricing once per room — used by display, CTA, and select button
                const roomPricing = calculatePricing(room.price, rules, config);
                const priceType = rules.canHaveSpecialPrice && rules.paywallEnabled ? 'special' : 'standard';

                return (
                <div
                  key={room.id}
                  className="border rounded-lg overflow-hidden hover:shadow-md transition-all"
                >
                  <div className="p-4">
                    {/* Room Header */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1">{room.name}</h3>
                        {room.bedConfiguration && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Bed className="h-4 w-4" />
                            <span>{room.bedConfiguration}</span>
                          </div>
                        )}
                        {room.size && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                            <Square className="h-4 w-4" />
                            <span>{room.size}</span>
                          </div>
                        )}
                        {room.view && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                            <Eye className="h-4 w-4" />
                            <span>{room.view}</span>
                          </div>
                        )}
                      </div>
                      <RoomPriceDisplay
                        apiPrice={room.price}
                        nights={nights}
                        rules={rules}
                        config={config}
                        isAuthenticated={isAuthenticated}
                      />
                    </div>

                    {/* Room Description */}
                    {room.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {room.description}
                      </p>
                    )}

                    {/* Room Amenities */}
                    {room.amenities && room.amenities.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {room.amenities.slice(0, 5).map((amenity) => {
                          const Icon = amenityIcons[amenity];
                          return (
                            <Badge key={amenity} variant="secondary" className="text-xs">
                              {Icon && <Icon className="h-3 w-3 mr-1" />}
                              {amenity}
                            </Badge>
                          );
                        })}
                        {room.amenities.length > 5 && (
                          <Badge variant="secondary" className="text-xs">
                            +{room.amenities.length - 5} more
                          </Badge>
                        )}
                      </div>
                    )}

                    {/* Occupancy Info */}
                    {room.maxOccupancy && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <Users className="h-4 w-4" />
                        <span>Max occupancy: {room.maxOccupancy} {room.maxOccupancy === 1 ? 'guest' : 'guests'}</span>
                      </div>
                    )}

                    <Separator className="my-3" />

                    {/* Cancellation Policy and Availability */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex-1">
                        {room.cancellationPolicy && (
                          <p className="text-sm">
                            <span className="font-medium">Cancellation:</span>{' '}
                            <span className={
                              room.cancellationPolicy.toLowerCase().includes('free') 
                                ? 'text-green-600 dark:text-green-400' 
                                : 'text-muted-foreground'
                            }>
                              {formatCancellationPolicy(room.cancellationPolicy)}
                            </span>
                          </p>
                        )}
                        {room.mealPlan && isDisplayableMealPlan(room.mealPlan) && (
                          <p className="text-sm text-muted-foreground mt-1">
                            <span className="font-medium">Meal:</span> {formatMealPlan(room.mealPlan)}
                          </p>
                        )}
                        {room.taxesOnArrival && room.taxesOnArrival.amount > 0 && (
                          <div className="text-sm text-muted-foreground mt-1">
                            <p>
                              <span className="font-medium">Taxes/fees paid on arrival:</span>{' '}
                              {formatCurrency(room.taxesOnArrival.amount, room.taxesOnArrival.currency)}
                            </p>
                            {room.taxesOnArrival.items && room.taxesOnArrival.items.length > 0 && (
                              <ul className="ml-4 mt-1 list-disc text-xs space-y-0.5">
                                {room.taxesOnArrival.items.map((item, i) => (
                                  <li key={i}>
                                    {item.name || 'Tax'}: {formatCurrency(item.amount, item.currency)}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                        {room.available !== undefined && room.available <= 5 && (
                          <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                            Only {room.available} {room.available === 1 ? 'room' : 'rooms'} left!
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Subscribe CTA for free users */}
                    {rules.paywallEnabled && isAuthenticated && !rules.canHaveSpecialPrice && (
                      <Alert className="mb-3 border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800">
                        <AlertDescription className="flex items-center justify-between text-sm">
                          <span className="text-emerald-700 dark:text-emerald-300">
                            Subscribe to save {formatCurrency(roomPricing.savings * nights)} on this room
                          </span>
                          <Button variant="outline" size="sm" asChild className="ml-2 border-emerald-300 text-emerald-700">
                            <Link to="/subscription">
                              <Sparkles className="h-3 w-3 mr-1" />
                              Upgrade
                            </Link>
                          </Button>
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Select Button */}
                    <Button
                      onClick={() => {
                        onSelectRoom(room, priceType, roomPricing);
                        onClose();
                      }}
                      className="w-full rounded-full"
                      size="lg"
                    >
                      Select This Room
                    </Button>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-muted-foreground mb-4">
                No rooms available for your selected dates.
              </p>
              <Button onClick={onClose} variant="outline">
                Close
              </Button>
            </div>
          )}
        </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}